import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Text,
  Image,
  FormControl,
  FormLabel,
  Input,
  Button,
  IconButton,
  useToast,
  Flex,
  Grid,
  GridItem,
  VStack,
  HStack,
  Badge,
  Divider,
  SimpleGrid,
  Tooltip,
  Switch,
  Skeleton,
  Heading,
  Icon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  Edit,
  Mail,
  CalendarDays,
  ShieldCheck,
  UserCircle2,
  Sun,
  Moon,
  Lock,
  LogIn,
  Trash2,
} from "lucide-react";

import { useAuth } from "../../auth/AuthContext";
import { db, storage } from "../../configs/firebase";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { normalizeUserRole, USER_ROLES } from "./constants/userRoles";

interface Member {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  photoURL?: string;
  createdAt?: string | number;
}

interface UserData {
  phone?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  createdAt?: number | string;
  photoURL?: string;
  displayName?: string;
  name?: string;
  email?: string;
  role?: string;
  status?: string;
  theme?: string;
  denguPoints?: number;

  businessName?: string;
  businessAddress?: string;
  businessPhone?: string;
  businessPhoneNumber?: string;
  logoURL?: string;
  fontColor?: string;

  accountName?: string;
  accountBank?: string;
  accountNumber?: string;
  accountBranch?: string;

  members?: Member[];
}

const cardsBorderRadii = "8px";
const trimValue = (value?: string | null) => String(value || "").trim();

const getBackendBaseUrl = () => {
  const configuredUrl = trimValue(process.env.REACT_APP_BACKEND_URL);

  if (configuredUrl) {
    return configuredUrl.replace(/\/+$/, "");
  }

  return window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://api.pickdengu.mw";
};

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const { theme, themeName, toggleTheme } = useCustomTheme();
  const toast = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<UserData | null>(null);

  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cancelDeleteRef = useRef<HTMLButtonElement | null>(null);

  const pageBg = theme.background;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;
  const subtleText = theme.textMuted;
  const tertiaryText = theme.textSubtle;
  const headingText = theme.text;
  const inputBg = theme.inputBg;
  const inputBorder = theme.inputBorder;
  const focusBorder = theme.inputBorderFocus;
  const cardShadow = theme.shadowSm;
  const panelShadow = theme.shadowMd;

  const isDark = theme.name === "dark";

  const effectiveRole = normalizeUserRole(userData?.role);
  const isSuperAdmin = effectiveRole === USER_ROLES.SUPER_ADMIN;
  const isAdminAccount = effectiveRole === USER_ROLES.ADMIN;

  const joinedDate = useMemo(() => {
    if (!userData?.createdAt) return "N/A";
    const raw = userData.createdAt;

    let d: Date;
    if (typeof raw === "number") d = new Date(raw);
    else if (/^\d+$/.test(String(raw))) d = new Date(parseInt(String(raw), 10));
    else d = new Date(String(raw));

    return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString();
  }, [userData?.createdAt]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data() as UserData;

          const savedProfilePhone = trimValue(data.phone || data.phoneNumber);
          const savedBusinessPhone = trimValue(
            data.businessPhoneNumber ||
              data.businessPhone ||
              data.whatsappNumber ||
              data.phoneNumber ||
              data.phone,
          );

          setUserData(data);
          setDisplayName(
            trimValue(user.displayName || data.displayName || data.name),
          );
          setPhone(savedProfilePhone);
          setBusinessPhone(savedBusinessPhone);
        } else {
          toast({
            title: "User data not found",
            status: "error",
            duration: 3000,
          });
        }
      } catch (error) {
        console.error("Error fetching user:", error);
        toast({
          title: "Error fetching user data",
          status: "error",
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [user, toast]);

  const handleSave = async () => {
    if (!user) return;

    const cleanDisplayName = trimValue(displayName);
    const cleanPhone = trimValue(phone);
    const cleanBusinessPhone = trimValue(businessPhone);

    setSaving(true);

    try {
      const userRef = doc(db, "users", user.uid);

      const payload: Record<string, any> = {
        phone: cleanPhone,
        phoneNumber: cleanPhone,
        displayName: cleanDisplayName,
        name: cleanDisplayName,

        // Business phone fields.
        // These are separate from the normal profile phone so business contact can be edited safely.
        businessPhone: cleanBusinessPhone,
        businessPhoneNumber: cleanBusinessPhone,
      };

      await updateDoc(userRef, payload);
      await updateProfile(user, { displayName: cleanDisplayName });

      setUserData((prev) =>
        prev
          ? {
              ...prev,
              phone: cleanPhone,
              phoneNumber: cleanPhone,
              displayName: cleanDisplayName,
              name: cleanDisplayName,
              businessPhone: cleanBusinessPhone,
              businessPhoneNumber: cleanBusinessPhone,
            }
          : null,
      );

      setDisplayName(cleanDisplayName);
      setPhone(cleanPhone);
      setBusinessPhone(cleanBusinessPhone);

      toast({
        title: "Profile updated",
        description: "Your personal and business contact details were saved.",
        status: "success",
        duration: 2500,
      });
    } catch (error: any) {
      console.error("Update failed:", error);

      toast({
        title: "Failed to update profile",
        description:
          error?.response?.data?.message ||
          error?.data?.message ||
          error?.message ||
          "Please try again.",
        status: "error",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleThemeToggle = async () => {
    if (!user) return;

    setThemeSaving(true);
    try {
      await toggleTheme();

      toast({
        title: `Theme changed to ${themeName === "dark" ? "light" : "dark"}`,
        status: "success",
        duration: 1800,
      });
    } catch (error) {
      console.error("Theme update failed:", error);
      toast({
        title: "Failed to update theme",
        status: "error",
        duration: 3000,
      });
    } finally {
      setThemeSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || deletingAccount) return;

    setDeletingAccount(true);

    try {
      /*
       * Always request a fresh Firebase ID token immediately before this
       * destructive operation. The backend verifies this token and derives
       * the UID from it, so the browser never chooses which account to delete.
       */
      const idToken = await user.getIdToken(true);

      if (!idToken) {
        throw new Error(
          "Could not verify your authentication session. Please sign in again.",
        );
      }

      const response = await fetch(`${getBackendBaseUrl()}/api/v1/auth/me`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({}),
      });

      const responseText = await response.text();

      let responseBody: any = null;

      if (responseText) {
        try {
          responseBody = JSON.parse(responseText);
        } catch {
          responseBody = responseText;
        }
      }

      if (!response.ok) {
        const message =
          responseBody &&
          typeof responseBody === "object" &&
          responseBody.message
            ? String(responseBody.message)
            : typeof responseBody === "string" && responseBody.trim()
              ? responseBody
              : `Account deletion failed with status ${response.status}.`;

        throw new Error(message);
      }

      setIsDeleteDialogOpen(false);

      /*
       * The backend has permanently removed the Firebase Authentication
       * identity and users/{uid} profile. Clear the local browser session too.
       */
      await logout().catch(() => undefined);

      toast({
        title: "Account deleted",
        description: "Your PickDengu account has been permanently deleted.",
        status: "success",
        duration: 3500,
        isClosable: true,
      });

      navigate("/login", {
        replace: true,
      });
    } catch (error: any) {
      console.error("Account deletion failed:", error);

      toast({
        title: "Could not delete account",
        description:
          error?.response?.data?.message ||
          error?.data?.message ||
          error?.message ||
          "Please try again.",
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user) return;

    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const storageRef = ref(storage, `profile_pictures/${user.uid}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        () => {},
        (error) => {
          console.error("Upload error:", error);
          toast({ title: "Upload failed", status: "error", duration: 3000 });
          setUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          await updateDoc(doc(db, "users", user.uid), {
            photoURL: downloadURL,
          });
          await updateProfile(user, { photoURL: downloadURL });

          setUserData((prev) =>
            prev ? { ...prev, photoURL: downloadURL } : null,
          );

          toast({
            title: "Profile photo updated",
            status: "success",
            duration: 2500,
          });

          setUploading(false);
        },
      );
    } catch (error) {
      console.error("Upload failed:", error);
      toast({ title: "Upload failed", status: "error", duration: 3000 });
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box bg={pageBg} minH="100%" p={{ base: 4, md: 6 }}>
        <Box mx="auto">
          <Skeleton h="170px" borderRadius={cardsBorderRadii} mb={6} />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Skeleton h="360px" borderRadius={cardsBorderRadii} />
            <Skeleton h="360px" borderRadius={cardsBorderRadii} />
          </SimpleGrid>
        </Box>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box
        bg={pageBg}
        minH="100%"
        p={{ base: 4, md: 6 }}
        color={theme.text}
        borderRadius={cardsBorderRadii}
      >
        <Box mx="auto">
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius={cardsBorderRadii}
            p={{ base: 4, md: 6 }}
            boxShadow={panelShadow}
            mb={6}
            position="relative"
            overflow="hidden"
          >
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              gap={4}
            >
              <Box>
                <HStack spacing={2} mb={2} flexWrap="wrap">
                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(39,183,255,0.14)" : "rgba(39,183,255,0.08)"
                    }
                    color={isDark ? "blue.200" : "blue.700"}
                    fontWeight="700"
                  >
                    Profile
                  </Badge>

                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(239,68,68,0.16)" : "rgba(239,68,68,0.10)"
                    }
                    color={isDark ? "red.200" : "red.700"}
                    fontWeight="700"
                  >
                    Login required
                  </Badge>
                </HStack>

                <Heading size="lg" color={headingText}>
                  Sign in to view your profile
                </Heading>

                <Text mt={2} color={subtleText} fontWeight="500" maxW="760px">
                  Access your personal details, business information, Dengu
                  Points, theme settings, and account preferences from one
                  secure place.
                </Text>
              </Box>

              <HStack
                spacing={2}
                px={3}
                py={2}
                borderRadius={cardsBorderRadii}
                border={`1px solid ${borderColor}`}
                bg={isDark ? "rgba(255,255,255,0.03)" : softBg}
                boxShadow={cardShadow}
              >
                <Box
                  w="8px"
                  h="8px"
                  borderRadius={cardsBorderRadii}
                  bg={theme.primary}
                />
                <Text color={headingText} fontWeight="700" fontSize="sm">
                  Secure account
                </Text>
              </HStack>
            </Flex>
          </Box>

          <Box
            borderRadius={cardsBorderRadii}
            bg={cardBg}
            boxShadow={cardShadow}
            border={`1px solid ${borderColor}`}
            p={{ base: 5, md: 6 }}
          >
            <VStack spacing={4} align="start">
              <Flex
                w="56px"
                h="56px"
                borderRadius={cardsBorderRadii}
                align="center"
                justify="center"
                bg={isDark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)"}
                border={`1px solid ${isDark ? "rgba(239,68,68,0.22)" : "rgba(239,68,68,0.16)"}`}
              >
                <Icon
                  as={Lock}
                  boxSize={6}
                  color={isDark ? "red.200" : "red.600"}
                />
              </Flex>

              <Box>
                <Text color={headingText} fontWeight="800" fontSize="lg">
                  You need to be logged in
                </Text>
                <Text color={subtleText} mt={1}>
                  Sign in to manage your profile, update your photo, change your
                  theme, and view your marketplace account details.
                </Text>
              </Box>

              <HStack spacing={3} pt={1} flexWrap="wrap">
                <Button
                  leftIcon={<LogIn size={16} />}
                  color={theme.textOnPrimary}
                  bgImage={theme.gradients?.primaryButton || theme.primary}
                  _hover={{
                    bgImage:
                      theme.gradients?.primaryButtonHover || theme.primaryHover,
                    transform: "translateY(-1px)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  borderRadius={cardsBorderRadii}
                  boxShadow={theme.shadowSm}
                  transition="all 0.2s ease"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>

                <Button
                  variant="outline"
                  borderColor={borderStrong}
                  color={headingText}
                  bg={cardBg}
                  _hover={{ bg: theme.hover }}
                  borderRadius={cardsBorderRadii}
                  onClick={() => navigate("/dashboard")}
                >
                  Go back
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      bg={pageBg}
      minH="100%"
      p={{ base: 4, md: 6 }}
      color={theme.text}
      borderRadius={cardsBorderRadii}
    >
      <Box mx="auto">
        <Box
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius={cardsBorderRadii}
          p={{ base: 4, md: 6 }}
          boxShadow={panelShadow}
          mb={6}
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            gap={4}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
          >
            <HStack spacing={4} align="center" minW={0}>
              <Flex position="relative" w="96px" h="96px" flexShrink={0}>
                <Image
                  src={
                    userData?.photoURL ||
                    user.photoURL ||
                    "https://bit.ly/sage-adebayo"
                  }
                  alt="Profile Picture"
                  boxSize="96px"
                  borderRadius={cardsBorderRadii}
                  objectFit="cover"
                  border="3px solid"
                  borderColor={theme.primary}
                  bg={softBg}
                />

                <Tooltip
                  label={uploading ? "Uploading..." : "Change photo"}
                  hasArrow
                >
                  <IconButton
                    aria-label="Edit Profile Picture"
                    icon={<Edit size={16} />}
                    size="sm"
                    position="absolute"
                    bottom={-1}
                    right={-1}
                    borderRadius={cardsBorderRadii}
                    bg={theme.primary}
                    color={theme.textOnPrimary}
                    _hover={{ bg: theme.primaryHover }}
                    onClick={() => fileInputRef.current?.click()}
                    isDisabled={uploading}
                    isLoading={uploading}
                  />
                </Tooltip>

                <input
                  type="file"
                  accept="image/*"
                  hidden
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </Flex>

              <VStack align="start" spacing={1} minW={0}>
                <HStack spacing={2} flexWrap="wrap">
                  <Text
                    fontSize={{ base: "xl", md: "2xl" }}
                    fontWeight="bold"
                    color={headingText}
                    noOfLines={1}
                  >
                    {displayName || userData?.name || "Profile"}
                  </Text>

                  <Badge
                    colorScheme={
                      isSuperAdmin
                        ? "red"
                        : isAdminAccount
                          ? "purple"
                          : effectiveRole === USER_ROLES.SHOP
                            ? "teal"
                            : "gray"
                    }
                    borderRadius={cardsBorderRadii}
                    px={2}
                    textTransform="capitalize"
                  >
                    {effectiveRole}
                  </Badge>
                </HStack>

                <HStack spacing={2} color={subtleText} fontSize="sm">
                  <Mail size={14} />
                  <Text noOfLines={1}>
                    {user.email || userData?.email || "No email"}
                  </Text>
                </HStack>

                <HStack spacing={2} color={subtleText} fontSize="sm">
                  <CalendarDays size={14} />
                  <Text>Joined {joinedDate}</Text>
                </HStack>
              </VStack>
            </HStack>

            <Box
              border="1px solid"
              borderColor={borderStrong}
              borderRadius={cardsBorderRadii}
              bg={softBg}
              p={3}
              minW={{ base: "100%", md: "250px" }}
            >
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="semibold" color={headingText}>
                    Theme
                  </Text>
                  <Text fontSize="xs" color={tertiaryText}>
                    App-wide appearance
                  </Text>
                </VStack>

                <HStack spacing={2} color={headingText}>
                  {themeName === "dark" ? (
                    <Moon size={16} />
                  ) : (
                    <Sun size={16} />
                  )}
                  <Switch
                    isChecked={themeName === "dark"}
                    onChange={handleThemeToggle}
                    colorScheme="teal"
                    isDisabled={themeSaving}
                  />
                </HStack>
              </HStack>
            </Box>
          </Flex>
        </Box>

        <Grid templateColumns={{ base: "1fr", lg: "1.1fr 0.9fr" }} gap={6}>
          <GridItem>
            <VStack spacing={6} align="stretch">
              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius={cardsBorderRadii}
                p={{ base: 4, md: 5 }}
                boxShadow={cardShadow}
              >
                <HStack mb={4} color={headingText}>
                  <UserCircle2 size={18} />
                  <Text fontWeight="bold" fontSize="lg">
                    Personal Information
                  </Text>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel color={subtleText}>Name</FormLabel>
                    <Input
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Your full name"
                      bg={inputBg}
                      color={theme.text}
                      borderColor={inputBorder}
                      _placeholder={{ color: theme.inputPlaceholder }}
                      _hover={{ borderColor: borderStrong }}
                      _focus={{
                        borderColor: focusBorder,
                        boxShadow: `0 0 0 1px ${focusBorder}`,
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={subtleText}>Email</FormLabel>
                    <Input
                      value={user.email || ""}
                      isReadOnly
                      bg={softBg}
                      color={theme.text}
                      borderColor={borderColor}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={subtleText}>Personal Phone</FormLabel>
                    <Input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Your personal phone number"
                      bg={inputBg}
                      color={theme.text}
                      borderColor={inputBorder}
                      _placeholder={{ color: theme.inputPlaceholder }}
                      _hover={{ borderColor: borderStrong }}
                      _focus={{
                        borderColor: focusBorder,
                        boxShadow: `0 0 0 1px ${focusBorder}`,
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={subtleText}>User ID</FormLabel>
                    <Input
                      value={user.uid}
                      isReadOnly
                      bg={softBg}
                      color={theme.text}
                      borderColor={borderColor}
                    />
                  </FormControl>
                </SimpleGrid>

                <Divider my={5} borderColor={theme.divider} />

                <HStack mb={4} color={headingText}>
                  <ShieldCheck size={18} />
                  <Text fontWeight="bold" fontSize="lg">
                    Business Contact
                  </Text>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel color={subtleText}>
                      Business Phone Number
                    </FormLabel>
                    <Input
                      value={businessPhone}
                      onChange={(e) => setBusinessPhone(e.target.value)}
                      placeholder="Your business phone number"
                      bg={inputBg}
                      color={theme.text}
                      borderColor={inputBorder}
                      _placeholder={{ color: theme.inputPlaceholder }}
                      _hover={{ borderColor: borderStrong }}
                      _focus={{
                        borderColor: focusBorder,
                        boxShadow: `0 0 0 1px ${focusBorder}`,
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={subtleText}>Business Name</FormLabel>
                    <Input
                      value={userData?.businessName || ""}
                      isReadOnly
                      placeholder="Business name"
                      bg={softBg}
                      color={theme.text}
                      borderColor={borderColor}
                    />
                  </FormControl>
                </SimpleGrid>

                <Divider my={5} borderColor={theme.divider} />

                <Flex
                  justify="space-between"
                  align={{ base: "start", md: "center" }}
                  direction={{ base: "column", md: "row" }}
                  gap={3}
                >
                  <Text fontSize="sm" color={subtleText}>
                    Keep your personal and business contact details up to date
                    for smoother bookings, orders, seller communication, and
                    WhatsApp notifications.
                  </Text>

                  <Button
                    bg={theme.primary}
                    color={theme.textOnPrimary}
                    _hover={{ bg: theme.primaryHover }}
                    onClick={handleSave}
                    isLoading={saving || uploading}
                    loadingText="Saving"
                    minW="160px"
                  >
                    Save Changes
                  </Button>
                </Flex>
              </Box>

              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius={cardsBorderRadii}
                p={{ base: 4, md: 5 }}
                boxShadow={cardShadow}
              >
                <HStack mb={4} color={headingText}>
                  <ShieldCheck size={18} />
                  <Text fontWeight="bold" fontSize="lg">
                    Access & Role
                  </Text>
                  {isSuperAdmin && (
                    <Badge colorScheme="red" borderRadius={cardsBorderRadii}>
                      Super Admin
                    </Badge>
                  )}
                  {isAdminAccount && (
                    <Badge colorScheme="purple" borderRadius={cardsBorderRadii}>
                      Admin
                    </Badge>
                  )}
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel color={subtleText}>Current Role</FormLabel>
                    <Input
                      value={effectiveRole}
                      isReadOnly
                      textTransform="capitalize"
                      bg={softBg}
                      color={theme.text}
                      borderColor={borderColor}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel color={subtleText}>Account Status</FormLabel>
                    <Input
                      value={userData?.status || "active"}
                      isReadOnly
                      textTransform="capitalize"
                      bg={softBg}
                      color={theme.text}
                      borderColor={borderColor}
                    />
                  </FormControl>
                </SimpleGrid>

                <Text mt={3} fontSize="sm" color={subtleText}>
                  Account roles are protected. Only a Super Admin can change a
                  user&apos;s platform role from the Users administration page.
                </Text>
              </Box>
            </VStack>
          </GridItem>

          <GridItem>
            <VStack spacing={6} align="stretch">
              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius={cardsBorderRadii}
                p={{ base: 4, md: 5 }}
                boxShadow={cardShadow}
              >
                <Text
                  fontWeight="bold"
                  fontSize="lg"
                  mb={4}
                  color={headingText}
                >
                  Profile Overview
                </Text>

                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                  <StatTile
                    label="Role"
                    value={effectiveRole}
                    capitalize
                    theme={theme}
                  />
                  <StatTile
                    label="Theme"
                    value={themeName}
                    capitalize
                    theme={theme}
                  />
                  <StatTile
                    label="Members"
                    value={
                      Array.isArray(userData?.members)
                        ? userData!.members!.length
                        : 0
                    }
                    theme={theme}
                  />
                  <StatTile label="Joined" value={joinedDate} theme={theme} />
                  <StatTile
                    label="Dengu Points"
                    value={userData?.denguPoints ?? 0}
                    theme={theme}
                  />
                </SimpleGrid>
              </Box>

              <Box
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius={cardsBorderRadii}
                p={{ base: 4, md: 5 }}
                boxShadow={cardShadow}
              >
                <Text
                  fontWeight="bold"
                  fontSize="lg"
                  mb={4}
                  color={headingText}
                >
                  Business Details
                </Text>

                <VStack align="stretch" spacing={3}>
                  <InfoRow
                    label="Business Name"
                    value={userData?.businessName || "—"}
                    theme={theme}
                  />
                  <InfoRow
                    label="Business Address"
                    value={userData?.businessAddress || "—"}
                    theme={theme}
                  />
                  <InfoRow
                    label="Business Phone Number"
                    value={
                      userData?.businessPhoneNumber ||
                      userData?.businessPhone ||
                      userData?.whatsappNumber ||
                      businessPhone ||
                      "—"
                    }
                    theme={theme}
                  />
                  <InfoRow
                    label="Personal Phone Number"
                    value={
                      userData?.phoneNumber || userData?.phone || phone || "—"
                    }
                    theme={theme}
                  />
                  <InfoRow
                    label="Font Color"
                    value={userData?.fontColor || "—"}
                    theme={theme}
                  />
                </VStack>
              </Box>
            </VStack>
          </GridItem>
        </Grid>

        <Box
          mt={6}
          bg={cardBg}
          border="1px solid"
          borderColor={isDark ? "rgba(239,68,68,0.40)" : "red.200"}
          borderRadius={cardsBorderRadii}
          p={{ base: 4, md: 5 }}
          boxShadow={cardShadow}
        >
          <Flex
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={4}
          >
            <HStack spacing={3} align="flex-start">
              <Flex
                w="42px"
                h="42px"
                flexShrink={0}
                align="center"
                justify="center"
                borderRadius={cardsBorderRadii}
                bg={isDark ? "rgba(239,68,68,0.14)" : "red.50"}
                border="1px solid"
                borderColor={isDark ? "rgba(239,68,68,0.28)" : "red.100"}
              >
                <Icon
                  as={Trash2}
                  boxSize={5}
                  color={isDark ? "red.200" : "red.600"}
                />
              </Flex>

              <Box>
                <Text
                  fontWeight="800"
                  fontSize="lg"
                  color={isDark ? "red.200" : "red.700"}
                >
                  Delete Account
                </Text>

                <Text mt={1} fontSize="sm" color={subtleText} maxW="760px">
                  Permanently delete your PickDengu sign-in account and profile.
                  This action cannot be undone.
                </Text>
              </Box>
            </HStack>

            <Button
              colorScheme="red"
              variant="outline"
              leftIcon={<Trash2 size={16} />}
              borderRadius={cardsBorderRadii}
              onClick={() => setIsDeleteDialogOpen(true)}
              isDisabled={deletingAccount}
              flexShrink={0}
            >
              Delete Account
            </Button>
          </Flex>
        </Box>
      </Box>

      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => {
          if (!deletingAccount) {
            setIsDeleteDialogOpen(false);
          }
        }}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent
            mx={4}
            bg={cardBg}
            color={headingText}
            borderRadius={cardsBorderRadii}
            border="1px solid"
            borderColor={borderColor}
          >
            <AlertDialogHeader fontSize="lg" fontWeight="800">
              Permanently delete your account?
            </AlertDialogHeader>

            <AlertDialogBody color={subtleText}>
              This will permanently delete your PickDengu authentication account
              and profile. You will no longer be able to sign in with this
              account. This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelDeleteRef}
                onClick={() => setIsDeleteDialogOpen(false)}
                isDisabled={deletingAccount}
                borderRadius={cardsBorderRadii}
              >
                Cancel
              </Button>

              <Button
                colorScheme="red"
                ml={3}
                onClick={handleDeleteAccount}
                isLoading={deletingAccount}
                loadingText="Deleting"
                borderRadius={cardsBorderRadii}
              >
                Delete Account
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

function InfoRow({
  label,
  value,
  theme,
}: {
  label: string;
  value: React.ReactNode;
  theme: any;
}) {
  return (
    <Flex justify="space-between" gap={4} align="start">
      <Text fontSize="sm" color={theme.textMuted}>
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="medium"
        textAlign="right"
        wordBreak="break-word"
        color={theme.text}
      >
        {value}
      </Text>
    </Flex>
  );
}

function StatTile({
  label,
  value,
  theme,
  capitalize = false,
}: {
  label: string;
  value: React.ReactNode;
  theme: any;
  capitalize?: boolean;
}) {
  return (
    <Box
      p={3}
      borderRadius={cardsBorderRadii}
      bg={theme.surfaceAlt}
      border="1px solid"
      borderColor={theme.border}
    >
      <Text fontSize="xs" color={theme.textSubtle}>
        {label}
      </Text>
      <Text
        fontWeight="bold"
        color={theme.text}
        textTransform={capitalize ? "capitalize" : undefined}
      >
        {value}
      </Text>
    </Box>
  );
}

export default ProfilePage;
